import React, { useMemo } from 'react';

import { Text } from '@betterment/samba-react';
import { Box, Button } from '@betterment/style-closet-react';
import { format, parseISO } from 'date-fns';
import { groupBy as _groupBy, times as _times } from 'es-toolkit/compat';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';

import { useAccount } from './AccountProvider';
import CheckingCard from './CheckingCard';
import CheckingLayout from './CheckingLayout';
import { useCustomer } from './CustomerProvider';
import { LockedBanner } from './LockedBanner';
import ManageTransactions from './ManageTransactions';
import { TRANSACTION_SUMMARY_LIMIT } from './SummaryTransactions';
import { TransactionFilters } from './TransactionFilters';
import TransactionLoader from './TransactionLoader';
import DetailedTransactionRow from './TransactionRow/DetailedTransactionRow';
import { useTransactions } from './TransactionsContext';
import { ResponsiveTable } from './TransactionsTable';

import ErrorFallback from '../../Shared/components/ErrorFallback';
import { Flash } from '../../Shared/contexts/FlashContext';
import { DateFilter, LegacyDateFilter } from '../../Shared/date-filter';
import { LockTypeCode, UnformattedTransactionsFilterParams } from '../data';

const Transactions: React.FC = () => {
  const { customer } = useCustomer();
  const { account } = useAccount();
  const location = useLocation();

  const { card } = customer;
  const isCardSystemLocked = card && card.lockTypeCode === LockTypeCode.SystemLocked;

  const legacyDateFilter: LegacyDateFilter =
    (qs.parse(location.search)?.dateFilter as LegacyDateFilter) || LegacyDateFilter.AllTime;

  const filterParams = qs.parse(location.search, { arrayFormat: 'bracket' }) as UnformattedTransactionsFilterParams;
  const { dateFilter = DateFilter.PastMonth, from, to, categories: selectedCategories = [] } = filterParams;

  const {
    canLoadMore,
    errors: transactionError,
    isLoading: isLoadingTransactions,
    loadData: loadTransactions,
    paginateTransactions,
    transactions,
    // @ts-ignore: js-runtime QueryParams time needs to be updated to allow for any value type
  } = useTransactions(account.id, { filter: filterParams });

  const groupedTransactions = useMemo(
    () => _groupBy(transactions, ({ createdAt }) => format(parseISO(createdAt), 'MMMM yyyy')),
    [transactions]
  );

  const hasNoTransactions = !isLoadingTransactions && transactions && transactions.length === 0;
  const isFilteredTransactionView =
    dateFilter !== DateFilter.AllTime || legacyDateFilter !== LegacyDateFilter.AllTime || selectedCategories.length > 0;

  const initialFilterValues = {
    from: from || '',
    to: to || '',
    dateFilter: dateFilter as DateFilter,
    legacyDateFilter,
    categories: selectedCategories,
  };

  const shouldDisableFilters = isLoadingTransactions && !transactionError;

  return (
    <CheckingLayout title="Transactions">
      {isCardSystemLocked && (
        <CheckingLayout.Banner>
          <LockedBanner accountLocked={false} cardLocked={true} />
        </CheckingLayout.Banner>
      )}
      <CheckingLayout.Slot>
        <Flash variant="success" />
        <Box pb="6" pt={[1, 1, 9]}>
          <CheckingCard mb={[6, 8]}>
            <TransactionFilters initialValues={initialFilterValues} disabled={shouldDisableFilters} />
            {transactionError && (
              <Box py="4">
                <ErrorFallback message="Unable to load transactions." handleReload={loadTransactions} />
              </Box>
            )}
            {Object.keys(groupedTransactions).map((month, groupIndex, groups) => {
              const isLastGroup = groupIndex === groups.length - 1;

              return (
                <Box key={month} mb={isLastGroup ? 0 : 8}>
                  <ResponsiveTable>
                    <thead>
                      <Box as="tr" py="8" borderBottom="1px solid" borderColor="bmtGrey20">
                        <Box as="th">
                          <Text
                            css={{
                              fontWeight: 'medium',
                            }}
                          >
                            {month}
                          </Text>
                        </Box>
                        <Box as="th" />
                      </Box>
                    </thead>
                    <Box as="tbody">
                      {groupedTransactions[month].map((txn, txnIndex, txnGroup) => {
                        const isLastTransaction = isLastGroup && txnIndex === txnGroup.length - 1;

                        return (
                          <DetailedTransactionRow
                            key={txn.id}
                            shouldDisplayBottomBorder={!isLastTransaction}
                            {...txn}
                          />
                        );
                      })}
                    </Box>
                  </ResponsiveTable>
                </Box>
              );
            })}
            {hasNoTransactions && (
              <Box bg="bmtWhite" px="8" py="135px">
                {isFilteredTransactionView ? (
                  <Box>
                    <Text
                      as="p"
                      css={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginBottom: 'space.100',
                      }}
                    >
                      No transactions found
                    </Text>
                    <Text
                      as="p"
                      color="text.weak"
                      css={{
                        fontSize: 'fontSize.50',
                        textAlign: 'center',
                        marginBottom: 'space.200',
                        _last: { marginBottom: 'space.0' },
                      }}
                    >
                      Adjust your filters to find transactions.
                    </Text>
                  </Box>
                ) : (
                  <>
                    <Text
                      as="p"
                      css={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginBottom: 'space.100',
                      }}
                    >
                      You have no transactions
                    </Text>
                    <Text
                      as="p"
                      color="text.weak"
                      css={{
                        fontSize: 'fontSize.50',
                        textAlign: 'center',
                        marginBottom: 'space.200',
                        _last: { marginBottom: 'space.0' },
                      }}
                    >
                      Fund your account to start spending.
                    </Text>
                  </>
                )}
              </Box>
            )}
            {canLoadMore && (
              <Box bg="bmtWhite" py="6" borderTop="1px solid" borderColor="bmtGrey20">
                <Button variant="tertiary" p="0" m="0" onClick={() => paginateTransactions(filterParams)}>
                  View more transactions
                </Button>
              </Box>
            )}

            {isLoadingTransactions && (
              <Box bg="bmtWhite" borderTop="1px solid" borderColor="bmtGrey20">
                {_times(TRANSACTION_SUMMARY_LIMIT, value => (
                  <TransactionLoader key={value} />
                ))}
              </Box>
            )}
          </CheckingCard>
        </Box>
      </CheckingLayout.Slot>
      <CheckingLayout.Slot order={[-1, -1, 0]}>
        <Box mb="6">
          <ManageTransactions isLoading={isLoadingTransactions} filterParams={filterParams} />
        </Box>
      </CheckingLayout.Slot>
    </CheckingLayout>
  );
};

export default Transactions;
